import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Bell, ShieldWarning, CheckCircle, ArrowRight, X,
    CaretDown, CaretUp, BellSlash,
} from "@phosphor-icons/react";

/**
 * Doctor portal notification bell.
 *
 * Reads /api/doctor/notifications every 60s and renders a popover of
 * cases that need the doctor to take action.
 *
 * What's new in this revision:
 *  1. **Grouping by kind** — 47 individual "plan ready" rows collapse
 *     into a single expandable summary ("47 plans ready to review").
 *     Each group can be drilled into to see individual rows.
 *  2. **Snooze for 1 hour** — power-user escape hatch when the doctor
 *     wants the badge silenced after they triage in the NeedsMeNow
 *     inbox. Snooze is localStorage-only (not server state) — a fresh
 *     login or a new tab still shows the live count. The cases are NOT
 *     hidden, just the bell badge + auto-popover.
 *  3. **"Mark all read"** is the user-facing label for the snooze
 *     action since the existing model has no per-item read state.
 */
const SNOOZE_MS = 60 * 60 * 1000;                // 1 hour
const SNOOZE_KEY = "doctor.notif.snoozed_until";


function getSnoozedUntil() {
    try {
        const v = Number(window.localStorage.getItem(SNOOZE_KEY));
        return v && v > Date.now() ? v : 0;
    } catch { return 0; }
}


const KIND_META = {
    review_required: {
        label: "Plans ready to review",
        Icon: CheckCircle,
        chipClass: "bg-violet-100 text-violet-700",
        target: "/doctor/cases?status=sent_for_approval",
    },
    info_requested: {
        label: "Questions from planner",
        Icon: ShieldWarning,
        chipClass: "bg-amber-100 text-amber-700",
        target: "/doctor/cases?status=info_requested",
    },
};


export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [snoozedUntil, setSnoozedUntil] = useState(getSnoozedUntil());
    const [expanded, setExpanded] = useState({});       // { kind: bool }
    const ref = useRef(null);
    const navigate = useNavigate();

    const load = () => {
        api.get("/doctor/notifications")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    // Re-check snooze status every 60s so the badge auto-restores
    // when the snooze window elapses.
    useEffect(() => {
        const t = setInterval(() => setSnoozedUntil(getSnoozedUntil()), 60000);
        return () => clearInterval(t);
    }, []);

    const goToCase = (target = "/doctor/cases") => {
        setOpen(false);
        navigate(target);
    };

    /* Group items by `kind` so the doctor sees a tidy "47 plans" summary
       row rather than 47 nearly-identical lines. The groups are sorted
       by count desc so the most-pressing kind is on top. */
    const groups = useMemo(() => {
        const map = new Map();
        for (const n of data.items || []) {
            const k = n.kind || "review_required";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(n);
        }
        return Array.from(map.entries())
            .map(([kind, items]) => ({ kind, items }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [data.items]);

    const liveCount = data.total || 0;
    const snoozedNow = snoozedUntil > Date.now();
    const visibleCount = snoozedNow ? 0 : liveCount;

    const markAllRead = () => {
        const until = Date.now() + SNOOZE_MS;
        try { window.localStorage.setItem(SNOOZE_KEY, String(until)); } catch (e) { /* storage unavailable */ }
        setSnoozedUntil(until);
        setOpen(false);
    };

    const unsnooze = () => {
        try { window.localStorage.removeItem(SNOOZE_KEY); } catch (e) { /* storage unavailable */ }
        setSnoozedUntil(0);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors"
                aria-label={`Notifications, ${visibleCount} item${visibleCount === 1 ? "" : "s"}`}
                data-testid="notification-bell"
            >
                <Bell size={18} weight={visibleCount > 0 ? "fill" : "regular"} className={visibleCount > 0 ? "text-primary" : "text-muted-foreground"} />
                {visibleCount > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums grid place-items-center border-2 border-card"
                        data-testid="notification-bell-badge"
                    >
                        {visibleCount > 99 ? "99+" : visibleCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[380px] bg-card border border-border rounded-2xl shadow-xl overflow-hidden z-50"
                    data-testid="notification-bell-panel"
                >
                    {/* Header — counts + Mark all read button */}
                    <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="font-display font-semibold text-sm">Action needed</div>
                            <div className="text-[11px] text-muted-foreground">
                                {liveCount === 0
                                    ? "Nothing waiting on you"
                                    : snoozedNow
                                        ? `${liveCount} item${liveCount === 1 ? "" : "s"} · snoozed`
                                        : `${liveCount} case${liveCount === 1 ? "" : "s"} need your input`}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {liveCount > 0 && !snoozedNow && (
                                <button
                                    type="button"
                                    onClick={markAllRead}
                                    className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid="notification-mark-all-read"
                                    title="Silences the badge for 1 hour. Items still live in your case list."
                                >
                                    <BellSlash size={11} weight="bold" />
                                    Mark all read
                                </button>
                            )}
                            {snoozedNow && (
                                <button
                                    type="button"
                                    onClick={unsnooze}
                                    className="text-[11px] font-semibold text-primary hover:underline"
                                    data-testid="notification-unsnooze"
                                >
                                    Un-snooze
                                </button>
                            )}
                            <button
                                type="button" onClick={() => setOpen(false)}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    {loading && (
                        <div className="px-4 py-6 text-sm text-muted-foreground text-center">Loading…</div>
                    )}

                    {!loading && groups.length === 0 && (
                        <div className="px-4 py-8 text-center" data-testid="notification-bell-empty">
                            <CheckCircle size={28} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                            <div className="text-sm font-semibold">You're all caught up</div>
                            <div className="text-[11px] text-muted-foreground mt-1">When a case needs your attention it will pop up here.</div>
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <div className="max-h-[440px] overflow-y-auto" data-testid="notification-bell-groups">
                            {groups.map((g) => {
                                const meta = KIND_META[g.kind] || KIND_META.review_required;
                                const isOpen = expanded[g.kind] || false;
                                return (
                                    <div key={g.kind} className="border-b border-border last:border-b-0" data-testid={`notification-group-${g.kind}`}>
                                        <button
                                            type="button"
                                            onClick={() => setExpanded((e) => ({ ...e, [g.kind]: !isOpen }))}
                                            className="w-full px-4 py-3 hover:bg-secondary/40 text-left flex items-center gap-3"
                                            data-testid={`notification-group-${g.kind}-header`}
                                        >
                                            <div className={`w-8 h-8 rounded-full ${meta.chipClass} grid place-items-center shrink-0`}>
                                                <meta.Icon size={14} weight="fill" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm">{meta.label}</div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                    {g.items.length} {g.items.length === 1 ? "case" : "cases"} · tap to {isOpen ? "collapse" : "expand"}
                                                </div>
                                            </div>
                                            <span className="text-[10px] tabular-nums font-bold bg-foreground text-card px-1.5 py-0.5 rounded">
                                                {g.items.length}
                                            </span>
                                            {isOpen
                                                ? <CaretUp size={12} className="text-muted-foreground shrink-0" />
                                                : <CaretDown size={12} className="text-muted-foreground shrink-0" />
                                            }
                                        </button>
                                        {isOpen && (
                                            <div className="bg-secondary/30">
                                                {g.items.slice(0, 8).map((n) => (
                                                    <NotificationRow key={n.id} n={n} onNavigate={() => goToCase(`/doctor/cases/${n.case_id}?tab=plan`)} />
                                                ))}
                                                {g.items.length > 8 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => goToCase(meta.target)}
                                                        className="w-full px-4 py-2.5 text-[11px] font-semibold text-primary hover:bg-secondary/50 border-t border-border inline-flex items-center justify-center gap-1"
                                                        data-testid={`notification-group-${g.kind}-see-all`}
                                                    >
                                                        See {g.items.length - 8} more <ArrowRight size={10} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <button
                            type="button"
                            onClick={() => goToCase("/doctor/cases")}
                            className="w-full px-4 py-3 text-xs font-semibold text-primary hover:bg-secondary/50 border-t border-border inline-flex items-center justify-center gap-1"
                            data-testid="notification-bell-view-all"
                        >
                            View all in Aligner Cases <ArrowRight size={11} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}


function NotificationRow({ n, onNavigate }) {
    const isInfo = n.kind === "info_requested";
    const Icon = isInfo ? ShieldWarning : CheckCircle;
    const accent = isInfo ? "text-amber-700 bg-amber-100" : "text-violet-700 bg-violet-100";
    return (
        <button
            type="button"
            onClick={onNavigate}
            className="w-full text-left px-4 py-2.5 hover:bg-card border-b last:border-b-0 border-border flex items-start gap-3"
            data-testid={`notification-item-${n.id}`}
        >
            <div className={`w-7 h-7 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={12} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{n.title}</div>
                    {n.case_code && <span className="font-mono text-[10px] px-1 rounded bg-card text-muted-foreground border border-border">{n.case_code}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{n.patient_name}</div>
                <div className="text-xs text-foreground/80 mt-1 line-clamp-2">{n.message}</div>
            </div>
        </button>
    );
}
